import EventEmitter from 'events';
import { ONLINE_STATE_CHANGED_EVENT } from './events';
const MOBILE_POLL_INTERVAL_MS = 20000;
/**
 * The network info service implementation for web (Chrome, Firefox and Safari).
 */
export default class NetworkInfoService extends EventEmitter {
    _onlineStateListener;
    _offlineStateListener;
    _connectionChangeListener;
    _mobilePollTimer = null;
    /**
     * Creates new instance...
     */
    constructor() {
        super();
        this._onlineStateListener = this._handleOnlineStatusChange.bind(this, /* online */ true);
        this._offlineStateListener = this._handleOnlineStatusChange.bind(this, /* offline */ false);
        this._connectionChangeListener = this._handleConnectionChange.bind(this);
    }
    /**
     * Returns connection quality details from the Network Information API if available.
     *
     * @private
     * @returns {object}
     */
    _getConnectionDetails() {
        const connection = navigator.connection
            || navigator.mozConnection
            || navigator.webkitConnection;
        if (!connection) {
            return {};
        }
        return {
            downlink: connection.downlink,
            effectiveType: connection.effectiveType,
            saveData: connection.saveData
        };
    }
    /**
     * Callback invoked when the Network Information API fires a change event.
     *
     * @private
     * @returns {void}
     */
    _handleConnectionChange() {
        this.emit(ONLINE_STATE_CHANGED_EVENT, {
            isOnline: navigator.onLine,
            details: this._getConnectionDetails()
        });
    }
    /**
     * Callback function to track the online state.
     *
     * @param {boolean} isOnline - Is the browser online or not.
     * @private
     * @returns {void}
     */
    _handleOnlineStatusChange(isOnline) {
        this.emit(ONLINE_STATE_CHANGED_EVENT, {
            isOnline,
            details: this._getConnectionDetails()
        });
    }
    /**
     * Returns true if this is a mobile browser (Android/iOS).
     *
     * @private
     * @returns {boolean}
     */
    _isMobileBrowser() {
        return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    /**
     * Polls network quality periodically on mobile browsers where the
     * Network Information API change events may not fire reliably.
     *
     * @private
     * @returns {void}
     */
    _pollNetworkQuality() {
        this.emit(ONLINE_STATE_CHANGED_EVENT, {
            isOnline: navigator.onLine,
            details: this._getConnectionDetails()
        });
    }
    /**
     * Checks for support.
     *
     * @returns {boolean}
     */
    static isSupported() {
        return Boolean(window.addEventListener) && typeof navigator.onLine !== 'undefined';
    }
    /**
     * Starts the service.
     *
     * @returns {void}
     */
    start() {
        window.addEventListener('online', this._onlineStateListener);
        window.addEventListener('offline', this._offlineStateListener);
        const connection = navigator.connection
            || navigator.mozConnection
            || navigator.webkitConnection;
        if (connection) {
            connection.addEventListener('change', this._connectionChangeListener);
        }
        // Emit initial state immediately
        this._handleConnectionChange();
        // On mobile browsers, poll periodically because connection change events are unreliable
        if (this._isMobileBrowser()) {
            this._mobilePollTimer = setInterval(this._pollNetworkQuality.bind(this), MOBILE_POLL_INTERVAL_MS);
        }
    }
    /**
     * Stops the service.
     *
     * @returns {void}
     */
    stop() {
        window.removeEventListener('online', this._onlineStateListener);
        window.removeEventListener('offline', this._offlineStateListener);
        const connection = navigator.connection
            || navigator.mozConnection
            || navigator.webkitConnection;
        if (connection) {
            connection.removeEventListener('change', this._connectionChangeListener);
        }
        if (this._mobilePollTimer !== null) {
            clearInterval(this._mobilePollTimer);
            this._mobilePollTimer = null;
        }
    }
}
